import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Grid, Text } from '@radix-ui/themes'
import { useAuth } from '~/auth/AuthProvider'
import { ImageLightbox } from '~/components/ImageLightbox'
import { PhotoThumbnail } from '~/features/installations/components/InstallationPhotos'
import { PhotoLightboxFooter } from '~/features/installations/components/PhotoLightboxFooter'
import type { MeterPhoto } from '../labels'
import { deleteMeterPhoto } from '../service'
import { useInvalidateMeterPhotos } from '../useInvalidateMeterPhotos'

export const MeterPhotoGrid = ({ photos }: { photos: MeterPhoto[] }) => {
  const { supabaseClient } = useAuth()
  const invalidate = useInvalidateMeterPhotos()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const openPhoto = openIndex === null ? null : photos[openIndex]

  const remove = useMutation({
    mutationFn: (photo: MeterPhoto) => deleteMeterPhoto(supabaseClient, photo),
    onSettled: invalidate
  })

  if (photos.length === 0) {
    return (
      <Text size="2" color="gray">
        No meter photo yet
      </Text>
    )
  }

  return (
    <>
      <Grid columns="4" gap="2">
        {photos.map((photo, index) => (
          <PhotoThumbnail key={photo.id} url={photo.url} alt="Meter" onOpen={() => setOpenIndex(index)} />
        ))}
      </Grid>
      {remove.error ? (
        <Text size="1" color="red">
          {remove.error.message}
        </Text>
      ) : null}
      <ImageLightbox
        images={photos.map((photo) => photo.url)}
        openIndex={openIndex}
        onOpenChange={setOpenIndex}
        footer={
          openPhoto ? (
            <PhotoLightboxFooter
              sharePath={openPhoto.storage_path}
              onDelete={{
                run: () => {
                  setOpenIndex(null)
                  remove.mutate(openPhoto)
                },
                confirmTitle: 'Delete photo?',
                confirmDescription: 'This permanently removes the meter photo. This cannot be undone.'
              }}
            />
          ) : null
        }
      />
    </>
  )
}
